import { CameraIcon, FileUploadIcon } from "@vericus/cadmus-icons";
import { Anchored, Button, LinkButton, Spacer, Text } from "@vericus/cadmus-ui";

import * as styles from "./work-attachment-section.css";

/**
 * Props for the AttachmentUploadControls component
 */
interface Props {
  /** Callback fired when the camera button is clicked */
  onCameraClick: () => void;
  /** Callback fired when the upload button is clicked */
  onUploadClick: () => void;
  /** Maximum file size in bytes */
  maxFileSize: number;
  /** Whether the controls should be disabled */
  disabled?: boolean;
  /** Whether files are currently being uploaded */
  isUploading?: boolean;
  /** Whether files are currently being deleted */
  isDeleting?: boolean;
  /** When true (LDB exam mode), hides the upload button and shows camera-only message */
  ldbExamMode?: boolean;
}

/**
 * AttachmentUploadControls component
 *
 * Displays the camera and upload buttons along with file size and format information.
 * In LDB exam mode, only the camera button is shown.
 */
export function AttachmentUploadControls({
  onCameraClick,
  onUploadClick,
  maxFileSize,
  disabled = false,
  isUploading = false,
  isDeleting = false,
  ldbExamMode = false,
}: Props) {
  const maxSizeMB = Math.round(maxFileSize / 1024 / 1024);

  return (
    <div className={styles.uploadSection}>
      <div className={styles.buttonGroup}>
        <Button
          kind="outline"
          size="md"
          onClick={onCameraClick}
          icon={<CameraIcon size={24} label="" />}
          disabled={disabled || isUploading || isDeleting}
        >
          CAMERA
        </Button>
        {!ldbExamMode && (
          <Button
            kind="secondary"
            size="md"
            icon={<FileUploadIcon size={24} label="" />}
            onClick={onUploadClick}
            disabled={disabled || isUploading || isDeleting}
          >
            {isUploading
              ? "UPLOADING..."
              : isDeleting
                ? "DELETING..."
                : "UPLOAD"}
          </Button>
        )}
      </div>

      <Spacer spacing={8} />

      <div className={styles.fileInfo}>
        <Text kind="bodyMd" color="grey600">
          Max size: {maxSizeMB} MB
        </Text>
        <Anchored
          href="https://support.cadmus.io/migration/file-uploads-and-image-capture"
          newTab
        >
          <LinkButton inline kind="primary">
            Learn more about the supported file formats.
          </LinkButton>
        </Anchored>
      </div>
    </div>
  );
}
